import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {Subscription} from 'rxjs';
import {SignalsNotificationService} from '@services/pages/interactive-data-analysis/signals-notification.service';
import {SignalField} from '@models/pages/interactive-data-analysis/interactive-data-analysis-signals';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';
import {InteractiveDataAnalysisConfig} from '@models/pages/interactive-data-analysis/interactive-data-analysis-config';
import {ArrayOperationService} from '@services/administration/array-operation/array-operation.service';
import {TranslateService} from '@ngx-translate/core';
import {InteractiveDataAnalysisProperty} from '@models/pages/interactive-data-analysis/interactive-data-analysis-property';
import {PaginateControlSettings} from '@models/paginate-control-settings';

@Component({
    selector: 'scitis-edit-interactive-data-analysis-signals',
    templateUrl: './edit-interactive-data-analysis-signals.component.html',
    styleUrls: ['./edit-interactive-data-analysis-signals.component.scss'],
    standalone: false
})
export class EditInteractiveDataAnalysisSignalsComponent implements OnInit, OnDestroy {

  @Input()
  public machineId: string | null = null;

  @Input()
  public useColorPalette: boolean = false;

  private subscription: Subscription = new Subscription();
  private configNotificationSubscription: Subscription = new Subscription();
  public signals: SignalField[] = [];
  public paginateSettings: PaginateControlSettings = new PaginateControlSettings({
    id: 'interactiveDataAnalysisSignalsId',
    itemsPerPage: 10,
  });
  public customItemsPerPageOptions: number[] = [5, this.paginateSettings.itemsPerPage];
  public loadingSignals: boolean = true;
  public interactiveDataAnalysisConfig: InteractiveDataAnalysisConfig | undefined;
  public colorPaletteUsedMessage = '';

  constructor(
    private activeModal: NgbActiveModal,
    private signalsNotificationService: SignalsNotificationService,
    private arrayOperationService: ArrayOperationService,
    private translateService: TranslateService,
  ) {
    this.colorPaletteUsedMessage = this.translateService.instant('Color palette is enabled.');
  }

  ngOnInit(): void {
    this.configureSignalNotification();
    this.configureIDAConfigNotification();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.configNotificationSubscription.unsubscribe();
  }

  public onClose(): void {
    this.activeModal.dismiss(ModalDialogDismissOption.CLOSE);
  }

  public onCancel(_$event: boolean): void {
    this.activeModal.dismiss(ModalDialogDismissOption.CANCEL);
  }


  public onSave(): void {
    this.activeModal.close(this.interactiveDataAnalysisConfig);
  }

  public onItemsPerPageChange(itemsPerPage: number): void {
    this.paginateSettings = {...this.paginateSettings, itemsPerPage: itemsPerPage, currentPage: 1};
  }

  public onLoadPageNumber(pageNumber: number): void {
    this.paginateSettings.currentPage = pageNumber;
  }

  private configureSignalNotification(): void {
    this.subscription = this.signalsNotificationService.signalsNotification
    .subscribe((signals: SignalField[] | undefined) => {
      this.signals = [];
      if (signals) {
        signals.forEach(signal => {
          if (!this.arrayOperationService.exists(signal, this.signals, 'id')) {
            this.signals.push(signal);
          }
        });
        this.loadingSignals = false;
      } else {
        this.loadingSignals = true;
      }
    });
  }

  private configureIDAConfigNotification(): void {
    this.configNotificationSubscription = this.signalsNotificationService.interactiveDataAnalysisConfigNotification
    .subscribe((interactiveDataAnalysisConfig: InteractiveDataAnalysisConfig | undefined) => {
      if (interactiveDataAnalysisConfig && interactiveDataAnalysisConfig.properties) {
        this.interactiveDataAnalysisConfig = interactiveDataAnalysisConfig;
      } else {
        this.initializeInteractiveDataAnalysisConfig();
      }
    });
  }

  private initializeInteractiveDataAnalysisConfig(): void {
    this.interactiveDataAnalysisConfig = new InteractiveDataAnalysisConfig();
    this.signals.forEach(signal => {
      this.interactiveDataAnalysisConfig?.properties.set(signal.id, new InteractiveDataAnalysisProperty(signal?.color));
    });
  }
}
